import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FileText,
  FolderTree,
  Tag as TagIcon,
  Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../contexts/PermissionContext';
import { PERMISSIONS } from '../../constants/permissions';
import {
  deletePost,
  publishPost,
  unpublishPost,
  getCategories,
  deleteCategory,
  getTags,
  deleteTag
} from '../../services/blogService';
import PostList from './components/PostList';
import CategoryList from './components/CategoryList';
import TagList from './components/TagList';
import PostEditPage from './pages/PostEditPage';
import CategoryEditPage from './pages/CategoryEditPage';
import TagEditPage from './pages/TagEditPage';
import { cn } from '../../lib/utils';

function BlogModule() {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  // Check if we're on a nested route (edit/create pages)
  const isNestedRoute = location.pathname.includes('/posts/') ||
    location.pathname.includes('/categories/') ||
    location.pathname.includes('/tags/');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const postListRef = useRef(null);

  const canManage = !!hasPermission(PERMISSIONS.BLOG.MANAGE);

  // Get tab from URL query param
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'categories') {
      setActiveTab(1);
    } else if (tabParam === 'tags') {
      setActiveTab(2);
    } else {
      setActiveTab(0);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchCategories();
    } else if (activeTab === 2) {
      fetchTags();
    }
  }, [activeTab]);

  // Load categories and tags for post form
  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  // ============================================
  // POSTS
  // ============================================

  const handleEditPost = (post = null) => {
    if (post) {
      navigate(`/panel/blog/posts/${post.id}/edit`);
    } else {
      navigate('/panel/blog/posts/new');
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Bu postu silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await deletePost(id);
      toast.success('Post başarıyla silindi');
      if (postListRef.current) {
        postListRef.current.refresh();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Post silinirken hata oluştu');
    }
  };

  const handlePublishPost = async (id) => {
    try {
      await publishPost(id);
      toast.success('Post yayınlandı');
      if (postListRef.current) {
        postListRef.current.refresh();
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      toast.error('Post yayınlanırken hata oluştu');
    }
  };

  const handleUnpublishPost = async (id) => {
    try {
      await unpublishPost(id);
      toast.success('Post yayından kaldırıldı');
      if (postListRef.current) {
        postListRef.current.refresh();
      }
    } catch (error) {
      console.error('Error unpublishing post:', error);
      toast.error('Post yayından kaldırılırken hata oluştu');
    }
  };

  // ============================================
  // CATEGORIES
  // ============================================

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getCategories({ tree: 'false' });
      if (response.state && response.data) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category = null) => {
    if (category) {
      navigate(`/panel/blog/categories/${category.id}/edit`);
    } else {
      navigate('/panel/blog/categories/new');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await deleteCategory(id);
      toast.success('Kategori başarıyla silindi');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Kategori silinirken hata oluştu');
    }
  };

  // ============================================
  // TAGS
  // ============================================

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await getTags({ limit: 100 });
      if (response.state && response.data) {
        setTags(response.data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Etiketler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = (tag = null) => {
    if (tag) {
      navigate(`/panel/blog/tags/${tag.id}/edit`);
    } else {
      navigate('/panel/blog/tags/new');
    }
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm('Bu etiketi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await deleteTag(id);
      toast.success('Etiket başarıyla silindi');
      fetchTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Etiket silinirken hata oluştu');
    }
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    const tabMap = { 0: '', 1: 'categories', 2: 'tags' };
    navigate(`/panel/blog${tabMap[newValue] ? `?tab=${tabMap[newValue]}` : ''}`);
  };

  // Render nested routes if we're on a nested path
  if (isNestedRoute) {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const blogIndex = pathParts.indexOf('blog');

    if (blogIndex !== -1 && pathParts.length > blogIndex + 1) {
      const nestedPath = pathParts.slice(blogIndex + 1).join('/');

      // Handle posts routes
      if (nestedPath.startsWith('posts/')) {
        if (nestedPath === 'posts/new') {
          return <PostEditPage />;
        }
        const postEditMatch = nestedPath.match(/^posts\/(\d+)\/edit$/);
        if (postEditMatch) {
          return <PostEditPage />;
        }
      }

      // Handle categories routes
      if (nestedPath.startsWith('categories/')) {
        if (nestedPath === 'categories/new') {
          return <CategoryEditPage />;
        }
        const categoryEditMatch = nestedPath.match(/^categories\/(\d+)\/edit$/);
        if (categoryEditMatch) {
          return <CategoryEditPage />;
        }
      }

      // Handle tags routes
      if (nestedPath.startsWith('tags/')) {
        if (nestedPath === 'tags/new') {
          return <TagEditPage />;
        }
        const tagEditMatch = nestedPath.match(/^tags\/(\d+)\/edit$/);
        if (tagEditMatch) {
          return <TagEditPage />;
        }
      }
    }
  }

  const tabs = [
    { label: 'Postlar', icon: FileText },
    { label: 'Kategoriler', icon: FolderTree },
    { label: 'Etiketler', icon: TagIcon }
  ];

  // Main List Page
  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-100">Blog Yönetimi</h1>
      </div>

      <div className="flex gap-1 p-1 bg-zinc-900/50 border border-zinc-800 rounded-lg w-full max-w-md">
        {tabs.map((tab, index) => {
          const Icon = tab.icon;
          const isActive = activeTab === index;
          return (
            <button
              key={index}
              onClick={() => handleTabChange(index)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-all rounded-md",
                isActive
                  ? "bg-zinc-800 text-zinc-100 shadow-sm"
                  : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {/* POSTS TAB */}
        {activeTab === 0 && (
          <PostList
            ref={postListRef}
            canManage={canManage}
            onEdit={handleEditPost}
            onDelete={handleDeletePost}
            onPublish={handlePublishPost}
            onUnpublish={handleUnpublishPost}
          />
        )}

        {/* CATEGORIES TAB */}
        {activeTab === 1 && (
          <CategoryList
            loading={loading}
            categories={categories}
            canManage={canManage}
            onAdd={() => handleEditCategory()}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
          />
        )}

        {/* TAGS TAB */}
        {activeTab === 2 && (
          <TagList
            loading={loading}
            tags={tags}
            canManage={canManage}
            onAdd={() => handleEditTag()}
            onEdit={handleEditTag}
            onDelete={handleDeleteTag}
          />
        )}
      </div>
    </div>
  );
}

export default BlogModule;
